import { useState } from "react";
import { Link } from "react-router-dom";
import { Hexagon, Tags, Scale } from "lucide-react";
import { Article } from "@/lib/types";
import { timeAgo } from "@/lib/api";
import { useEntityRegistry, resolveEntity, entityNavRoute, entityTrack } from "@/lib/entities";
import { TopicLink } from "@/components/entity/TopicLink";
import { ProtocolLogo } from "@/components/sector/ProtocolLogo";
import { RegulationRef } from "@/components/article/ContextRail";

const GENERIC_TAGS = new Set(["web3", "crypto", "blockchain", "markets", "news", "cryptocurrency", "finance"]);

const REG_DOT: Record<string, string> = {
  enforced: "#34D399", enacted: "#34D399", proposed: "#60A5FA",
  in_review: "#00E5FF", review: "#00E5FF", pending: "#FBBF24",
};

const RowLabel = ({ icon: Icon, children }: { icon: any; children: string }) => (
  <p className="flex items-center gap-1.5 font-mono text-[8.5px] font-bold uppercase tracking-[0.24em] text-gray-500">
    <Icon className="h-3 w-3 text-[#A78BFA]" aria-hidden /> {children}
  </p>
);

/* Right-rail module 2 (§24-26): content-aware and variable-height.
   Rows without verified data are omitted; sentiment stays hidden until
   NewsLayer has a defensible per-article signal. */
export const ArticleIntelPanel = ({ article, regulationRef }: {
  article: Article; regulationRef?: RegulationRef;
}) => {
  const { data: reg } = useEntityRegistry();
  const [expanded, setExpanded] = useState(false);

  const mentions = ((article as any).protocol_mentions || []) as { slug: string; protocol: string }[];
  const mentionNames = new Set(mentions.map((m) => m.protocol.toLowerCase()));
  const plainEntities = (article.entities || []).filter((e) => !mentionNames.has(e.toLowerCase()));
  const chips: { key: string; label: string; kind: "entity" | "topic"; to: string | null; logo?: string; entitySlug?: string }[] = [
    ...mentions.map((m) => ({
      key: `p-${m.slug}`, label: m.protocol, kind: "entity" as const,
      to: entityNavRoute(resolveEntity(reg, "protocol", m.slug)), logo: m.slug, entitySlug: m.slug,
    })),
    ...plainEntities.map((e) => ({ key: `e-${e}`, label: e, kind: "topic" as const, to: null })),
  ];
  const visibleChips = expanded ? chips : chips.slice(0, 4);
  const hiddenCount = chips.length - visibleChips.length;

  const entityLower = new Set((article.entities || []).map((e) => e.toLowerCase()));
  const topics = (article.tags || [])
    .filter((t) => !entityLower.has(t.toLowerCase()))
    .sort((a, b) => Number(GENERIC_TAGS.has(a.toLowerCase())) - Number(GENERIC_TAGS.has(b.toLowerCase())))
    .slice(0, 5);

  const tracked = ((article as any).tracked_regulations || []) as { title: string; slug?: string; status?: string }[];
  const regSignal = regulationRef
    ? { title: regulationRef.title, slug: regulationRef.slug, status: regulationRef.status, updated_at: regulationRef.updated_at }
    : tracked[0] || null;

  if (!chips.length && !topics.length && !regSignal) return null;

  return (
    <section data-testid="article-intel-panel" data-track="article_intelligence"
      className="rounded-xl border border-white/[0.08] bg-[#0a0b10] p-4 space-y-4">
      <p className="font-mono text-[9px] font-bold uppercase tracking-[0.28em] text-[#A78BFA]">Article Intelligence</p>

      {chips.length > 0 && (
        <div data-testid="intel-key-entities">
          <RowLabel icon={Hexagon}>Key Entities</RowLabel>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {visibleChips.map((c) => c.kind === "topic" ? (
              <TopicLink key={c.key} topic={c.label} />
            ) : c.to ? (
              <Link key={c.key} to={c.to} data-testid={`entity-chip-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => c.entitySlug && entityTrack("entity_nav_click", `protocol:${c.entitySlug}`, { surface: "article_intel_panel" })}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.12] bg-white/[0.04] px-2.5 py-1 text-[12px] font-semibold text-gray-200 hover:border-[#00E5FF]/50 hover:text-white transition-colors capitalize focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#00E5FF]/60">
                {c.logo && <ProtocolLogo slug={c.logo} name={c.label} size={16} />}
                {c.label}
              </Link>
            ) : (
              <span key={c.key} data-testid={`entity-chip-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} data-live="false"
                className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.12] bg-white/[0.04] px-2.5 py-1 text-[12px] font-semibold text-gray-200 capitalize">
                {c.logo && <ProtocolLogo slug={c.logo} name={c.label} size={16} />}
                {c.label}
              </span>
            ))}
            {hiddenCount > 0 && (
              <button type="button" onClick={() => setExpanded(true)} data-testid="entity-chips-more"
                className="rounded-full border border-white/[0.08] px-2.5 py-1 font-mono text-[10px] text-gray-500 hover:text-white transition-colors">
                +{hiddenCount} more
              </button>
            )}
          </div>
        </div>
      )}

      {topics.length > 0 && (
        <div data-testid="intel-topics">
          <RowLabel icon={Tags}>Topics</RowLabel>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {topics.map((t) => (
              <Link key={t} to={`/search?q=${encodeURIComponent(t)}`} data-testid={`topic-chip-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="rounded-full border border-white/[0.08] bg-transparent px-2.5 py-1 text-[11.5px] text-gray-400 hover:text-white hover:border-white/25 transition-colors capitalize">
                {t}
              </Link>
            ))}
          </div>
        </div>
      )}

      {regSignal && (
        <div data-testid="intel-regulation-signal">
          <RowLabel icon={Scale}>Regulation Signal</RowLabel>
          <div className="mt-2">
            <p className="flex items-center gap-2">
              <span aria-hidden className="h-2 w-2 rounded-full shrink-0"
                style={{ background: REG_DOT[(regSignal.status || "").toLowerCase()] || "#9CA3AF" }} />
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-gray-200">
                {(regSignal.status || "tracked").replace(/_/g, " ")}
              </span>
              {(regSignal as any).updated_at && (
                <span className="font-mono text-[9px] text-gray-600">Updated {timeAgo((regSignal as any).updated_at)}</span>
              )}
            </p>
            {regSignal.slug ? (
              <Link to={`/regulation/${regSignal.slug}`} data-testid="intel-regulation-link"
                className="mt-1.5 block text-[12.5px] text-gray-300 leading-snug line-clamp-2 hover:text-[#00E5FF] transition-colors">
                {regSignal.title}
              </Link>
            ) : (
              <p className="mt-1.5 text-[12.5px] text-gray-300 leading-snug line-clamp-2">{regSignal.title}</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
};
